import { parseEther, toBytes } from 'viem'
import {
  ButtonMenu,
  Button,
  CloseIcon,
  Heading,
  Text,
  IconButton,
  InjectedModalProps,
  ModalBody,
  ModalWrapper as BaseModalWrapper,
  ModalTitle,
  ModalCloseButton,
  Flex,
  FlexGap,
  CopyIcon,
  OpenNewIcon,
  Box,
  Input,
  CheckmarkCircleIcon,
  CheckmarkCircleFillIcon
} from '@pancakeswap/uikit'
import { useAccount, useBalance } from 'wagmi'
import { useState, useCallback, useMemo, useEffect, useRef, HTMLInputTypeAttribute } from 'react'
import { useTranslation } from '@pancakeswap/localization'
import { styled } from 'styled-components'
import useAuth from 'hooks/useAuth'
import { CurrencyLogo } from 'components/Logo'
import useNativeCurrency from 'hooks/useNativeCurrency'

import { MAXIMUM_QUICK_BUY_AMOUNT, useUserQuickBuyAmount } from '@pancakeswap/utils/user'
import { base58 } from 'ethers/lib/utils'
import { ENumericalInput } from '@pancakeswap/widgets-internal'
import { formatDecimals } from '@pancakeswap/utils/formatBalance'
import WalletInfo from './WalletInfo'
import WalletTransactions from './WalletTransactions'
import WalletWrongNetwork from './WalletWrongNetwork'

export enum WalletView {
  WALLET_INFO,
  TRANSACTIONS,
  WRONG_NETWORK,
}

interface WalletModalProps extends InjectedModalProps {
  initialView?: WalletView
  isTopRight?: boolean // Add this new prop
}

export const LOW_NATIVE_BALANCE = parseEther('0.002', 'wei')

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #958BB0;
  margin: 0;
  opacity: 0.5;
`

const Tabs = styled.div`
  background-color: transparent;
  border-bottom: 1px solid ${({ theme }) => theme.colors.cardBorder};
  padding: 16px 24px;
`
const ButtonMenuItem = styled(Button)`
  width: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: transparent;
`

interface TabsComponentProps {
  view: WalletView
  handleClick: (newIndex: number) => void
}

const TabsComponent: React.FC<React.PropsWithChildren<TabsComponentProps>> = ({ view, handleClick }) => {
  const { t } = useTranslation()

  return (
    <Tabs>
      <ButtonMenu scale="sm" variant="subtle" onItemClick={handleClick} activeIndex={view} fullWidth>
        <ButtonMenuItem>Wallet</ButtonMenuItem>
        <ButtonMenuItem>Transactions</ButtonMenuItem>
      </ButtonMenu>
    </Tabs>
  )
}

const ModalWrapper = styled(BaseModalWrapper)`
  width: 100%;
  // min-height: 100vh;
  background: #332D3E;
  margin: 0;
  
  ${({ theme }) => theme.mediaQueries.sm} {
    width: 520px;
    min-height: auto;
    max-height: 90vh;
    border-radius: 16px;
    margin: auto;
  }
`

const StyledModalBody = styled(ModalBody)`
  padding: 0;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  background: #332D3E;
  display: flex;
  flex-direction: column;
`

const ContentWrapper = styled(Flex)`
  flex-direction: column;
  flex: 1;
  min-height: 0;

  ${({ theme }) => theme.mediaQueries.sm} {
    min-height: auto;
  }
`

const AddressSection = styled(Flex)`
  padding: 16px;
  flex-direction: column;
  gap: 8px;

  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 32px 24px;
    gap: 16px;
    flex-direction: column;
    align-items: center;
  }
`

const AddressRow = styled(Flex)`
  width: 100%;
  justify-content: flex-start;
  flex-direction: row;
  align-items: center;
  gap: 8px;

  ${({ theme }) => theme.mediaQueries.sm} {
    flex-direction: column;
    justify-content: center;
    gap: 16px;
  }
`

const AddressText = styled(FlexGap)`
  align-items: center;
  gap: 8px;
  // background: #0003;
  // border-radius: 16px;
  // padding: 2px 12px;

  ${({ theme }) => theme.mediaQueries.sm} {
    justify-content: center;
  }
`

const StyledDisconnectButton = styled(Button)`
  height: 46px;
  padding: 12px 20px;
  gap: 6px;
  border-radius: 16px;
  background: #FFFFFF1A;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &:hover:not(:disabled) {
    background: #FFFFFF1A;
    opacity: 0.7;
  }
`

const DisconnectWrapper = styled(Box)`
  padding: 0 16px 12px;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.sm} {
    display: none;
  }

  ${StyledDisconnectButton} {
    width: 100%;
  }
`

const DesktopDisconnectWrapper = styled(Box)`
  display: none;
  padding: 12px 24px 24px;
  width: 100%;

  ${({ theme }) => theme.mediaQueries.sm} {
    display: block;
    margin-top: auto;
  }
`

const ModalHeader = styled(Flex)`
  padding: 10px;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid var(--Gray-500, #423B51);
  background: var(--Gray-600, #3A3447);

  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 10px 16px;
  }
`

const StyledIconButton = styled(IconButton)`
  width: 24px;
  height: 24px;
  min-width: 24px;
  padding: 0;

  > svg {
    width: 16px;
    height: 16px;
  }
`

const QuickBuySection = styled(Flex)`
  padding: 16px;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  position: relative;
  flex-direction: row;

  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 16px 24px;
  }

  &:before, &:after {
    content: '';
    position: absolute;
    left: 16px;
    right: 16px;
    height: 1px;
    background: rgba(255, 255, 255, 0.1);

    ${({ theme }) => theme.mediaQueries.sm} {
      left: 24px;
      right: 24px;
    }
  }

  &:before {
    top: 0;
  }

  &:after {
    bottom: 0;
  }
`

const TransactionSection = styled(Box)`
  padding: 16px;
  padding-bottom: 0px;
  
  > div:first-child {
    margin-bottom: 8px;
  }
  
  ${({ theme }) => theme.mediaQueries.sm} {
    padding: 24px;
    padding-bottom: 0px;
    
    > div:first-child {
      margin-bottom: 16px;
    }
  }
`

const TransactionHeader = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  
  ${Text} {
    font-family: 'IBM Plex Sans';
    font-size: 14px;
    color: #FFFFFF;
    font-weight: 600;
  }
`

const StyledButtonMenuItem = styled(ButtonMenuItem)`
  background: transparent;
  width: 100%;
  color: white;
  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }
`

const HiddenSpan = styled.span`
  opacity: 0.5;
  font-size: 13px;
  font-weight: 600;
`

const StyledInput = styled(ENumericalInput)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: #413C4C;
  border: none;
  width: 100%;
  text-align: right;
  padding: 0;
  // padding-right: 4px;
  color: white;
  font-size: 13px;
  font-weight: 600;
  
  ${({ theme }) => theme.mediaQueries.sm} {
    width: 100px;
  }
  
  &:focus:not(:disabled) {
    box-shadow: none;
  }
`

const AmountInputContainer = styled(Flex)`
  background: #413C4C;
  border-radius: 8px;
  padding: 4px 8px;
  padding-left: 16px;
  gap: 8px;
  align-items: center;
  // min-width: 80px;
  width: auto;
  // max-width: 120px;

  // ${({ theme }) => theme.mediaQueries.sm} {
  //   min-width: 100px;
  //   max-width: none;
  // }
`

const QuickAmount = ({ amount, setAmount }: { amount: number, setAmount: (amount: number) => void }) => {
  const [content, setContent] = useState(formatDecimals(amount))

  const handleUserInput = (value) => {
    setAmount(Number(value))
    setContent(formatDecimals(value))
  }

  return (
    <Box position="relative">
      <HiddenSpan>{content}</HiddenSpan>
      <StyledInput
        type="text"
        value={amount}
        onUserInput={handleUserInput}
      />
    </Box>
  )
}

const WalletModal: React.FC<React.PropsWithChildren<WalletModalProps>> = ({
  initialView = WalletView.WALLET_INFO,
  onDismiss,
  isTopRight = false,
}) => {
  const { t } = useTranslation()
  const { address: account } = useAccount()
  // const { data, isFetched } = useBalance({ address: account })
  // const hasLowNativeBalance = isFetched && data && data.value <= LOW_NATIVE_BALANCE
  const { logout } = useAuth()
  // const transactions = useAllSortedRecentTransactions()
  const [copied, setCopied] = useState<string>()
  // const { chainId } = useActiveChainId()
  const native = useNativeCurrency()
  const [quickBuyAmount, setQuickBuyAmount] = useUserQuickBuyAmount()

  const referralCode = useMemo(() => {
    return base58.encode(toBytes(account))
  }, [account])

  const basePath = useMemo(() => {
    return `${window.location.origin}/invite/`
  }, [])

  const referralPath = useMemo(() => {
    return `${basePath}${referralCode}`
  }, [basePath, referralCode])

  const handleCopy = (str: string) => {
    // if (account) {
    navigator.clipboard.writeText(str)
    setCopied(str)
    setTimeout(() => setCopied(undefined), 1000)
    // }
  }

  // const formatNumber = (value: string) => {
  //   const number = Number(value)
  //   if (Number.isNaN(number)) return ''
  //   return number.toLocaleString()
  // }

  const handleAmountChange = (amount: number) => {
    if (amount > MAXIMUM_QUICK_BUY_AMOUNT)
      setQuickBuyAmount(MAXIMUM_QUICK_BUY_AMOUNT)
    else
      setQuickBuyAmount(amount)
  }

  return (
    <ModalWrapper>
      <StyledModalBody>
        <ContentWrapper>
          <ModalHeader>
            <Text marginLeft="10px" color="#C1BDCB">My Wallet</Text>
            <ModalCloseButton onDismiss={onDismiss} />
          </ModalHeader>

          <Flex justifyContent={["flex-start", "flex-start", "center"]} alignItems="center" py="16px" px={["16px", "16px", "24px"]}>
            <Flex alignItems="center" justifyContent="center" width={["40px", "45px", "62px"]} height={["40px", "45px", "62px"]} style={{ borderRadius: '50%', background: '#FFFFFF12', marginRight: '16px' }}>
              <svg width="60%" height="60%" viewBox="0 0 38 38" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.06234 31.3215L8.51633 30.4305H8.51633L8.06234 31.3215ZM6.67846 29.9376L7.56947 29.4837H7.56947L6.67846 29.9376ZM29.9376 31.3215L29.4836 30.4305L29.4836 30.4305L29.9376 31.3215ZM31.3215 29.9376L30.4305 29.4836V29.4837L31.3215 29.9376ZM29.9376 9.84515L29.4836 10.7362L29.4836 10.7362L29.9376 9.84515ZM31.3215 11.229L30.4305 11.683V11.683L31.3215 11.229ZM8.06234 9.84515L7.60835 8.95414H7.60835L8.06234 9.84515ZM6.67846 11.229L5.78745 10.775H5.78745L6.67846 11.229ZM24.6145 23.5774L25.0685 22.6864L24.6145 23.5774ZM23.9226 22.8855L24.8136 22.4315L23.9226 22.8855ZM34.6607 22.8855L33.7697 22.4315L34.6607 22.8855ZM33.9688 23.5774L33.5148 22.6864L33.9688 23.5774ZM33.9688 17.5892L33.5148 18.4802L33.9688 17.5892ZM34.6607 18.2812L33.7697 18.7352L34.6607 18.2812ZM24.6145 17.5892L25.0685 18.4802L24.6145 17.5892ZM23.9226 18.2812L24.8136 18.7352L23.9226 18.2812ZM9.12859 8.57153C8.61581 8.77664 8.36639 9.35861 8.5715 9.87139C8.77662 10.3842 9.35859 10.6336 9.87137 10.4285L9.12859 8.57153ZM23.1619 4.03522L22.7906 3.10674V3.10674L23.1619 4.03522ZM24.3333 9.5C24.3333 10.0523 24.781 10.5 25.3333 10.5C25.8856 10.5 26.3333 10.0523 26.3333 9.5H24.3333ZM11.4 10.5H26.6V8.5H11.4V10.5ZM26.6 30.6667H11.4V32.6667H26.6V30.6667ZM7.33331 26.6V14.5667H5.33331V26.6H7.33331ZM30.6666 14.5667V17.8125H32.6666V14.5667H30.6666ZM30.6666 24.0469V26.6H32.6666V24.0469H30.6666ZM11.4 30.6667C10.4967 30.6667 9.87943 30.6659 9.40155 30.6268C8.93534 30.5888 8.69056 30.5193 8.51633 30.4305L7.60835 32.2125C8.11151 32.4689 8.6488 32.572 9.23869 32.6202C9.8169 32.6674 10.5297 32.6667 11.4 32.6667V30.6667ZM5.33331 26.6C5.33331 27.4703 5.33254 28.1831 5.37978 28.7613C5.42797 29.3512 5.53108 29.8885 5.78745 30.3916L7.56947 29.4837C7.48069 29.3094 7.41123 29.0646 7.37314 28.5984C7.33409 28.1206 7.33331 27.5033 7.33331 26.6H5.33331ZM8.51633 30.4305C8.10865 30.2228 7.77719 29.8913 7.56947 29.4837L5.78745 30.3916C6.18693 31.1756 6.82435 31.8131 7.60835 32.2125L8.51633 30.4305ZM26.6 32.6667C27.4702 32.6667 28.1831 32.6674 28.7613 32.6202C29.3512 32.572 29.8884 32.4689 30.3916 32.2125L29.4836 30.4305C29.3094 30.5193 29.0646 30.5888 28.5984 30.6268C28.1205 30.6659 27.5032 30.6667 26.6 30.6667V32.6667ZM30.6666 26.6C30.6666 27.5033 30.6659 28.1206 30.6268 28.5984C30.5887 29.0646 30.5193 29.3094 30.4305 29.4836L32.2125 30.3916C32.4689 29.8885 32.572 29.3512 32.6202 28.7613C32.6674 28.1831 32.6666 27.4703 32.6666 26.6H30.6666ZM30.3916 32.2125C31.1756 31.8131 31.813 31.1756 32.2125 30.3916L30.4305 29.4837C30.2228 29.8913 29.8913 30.2228 29.4836 30.4305L30.3916 32.2125ZM26.6 10.5C27.5032 10.5 28.1205 10.5008 28.5984 10.5398C29.0646 10.5779 29.3094 10.6474 29.4836 10.7362L30.3916 8.95414C29.8884 8.69777 29.3512 8.59466 28.7613 8.54647C28.1831 8.49923 27.4702 8.5 26.6 8.5V10.5ZM32.6666 14.5667C32.6666 13.6964 32.6674 12.9836 32.6202 12.4054C32.572 11.8155 32.4689 11.2782 32.2125 10.775L30.4305 11.683C30.5193 11.8572 30.5887 12.102 30.6268 12.5682C30.6659 13.0461 30.6666 13.6634 30.6666 14.5667H32.6666ZM29.4836 10.7362C29.8913 10.9439 30.2228 11.2753 30.4305 11.683L32.2125 10.775C31.813 9.99103 31.1756 9.35361 30.3916 8.95414L29.4836 10.7362ZM11.4 8.5C10.5297 8.5 9.8169 8.49923 9.23869 8.54647C8.6488 8.59466 8.11151 8.69777 7.60835 8.95414L8.51633 10.7362C8.69056 10.6474 8.93534 10.5779 9.40155 10.5398C9.87943 10.5008 10.4967 10.5 11.4 10.5V8.5ZM7.33331 14.5667C7.33331 13.6634 7.33409 13.0461 7.37314 12.5682C7.41123 12.102 7.48069 11.8572 7.56947 11.683L5.78745 10.775C5.53108 11.2782 5.42797 11.8155 5.37978 12.4054C5.33254 12.9836 5.33331 13.6964 5.33331 14.5667H7.33331ZM7.60835 8.95414C6.82434 9.35361 6.18692 9.99103 5.78745 10.775L7.56947 11.683C7.77719 11.2753 8.10865 10.9439 8.51633 10.7362L7.60835 8.95414ZM26.2833 18.4167H32.3V16.4167H26.2833V18.4167ZM33.8333 19.95V21.2167H35.8333V19.95H33.8333ZM32.3 22.75H26.2833V24.75H32.3V22.75ZM24.75 21.2167V19.95H22.75V21.2167H24.75ZM26.2833 22.75C25.8234 22.75 25.5387 22.7492 25.3248 22.7318C25.1226 22.7152 25.0734 22.6889 25.0685 22.6864L24.1605 24.4684C24.4943 24.6385 24.8361 24.6985 25.1619 24.7251C25.4761 24.7508 25.8564 24.75 26.2833 24.75V22.75ZM22.75 21.2167C22.75 21.6435 22.7492 22.0238 22.7749 22.338C22.8015 22.6639 22.8615 23.0057 23.0315 23.3395L24.8136 22.4315C24.8111 22.4266 24.7847 22.3774 24.7682 22.1752C24.7508 21.9613 24.75 21.6765 24.75 21.2167H22.75ZM25.0685 22.6864C24.9587 22.6305 24.8695 22.5413 24.8136 22.4315L23.0315 23.3395C23.2792 23.8256 23.6744 24.2208 24.1605 24.4684L25.0685 22.6864ZM33.8333 21.2167C33.8333 21.6765 33.8325 21.9613 33.8151 22.1752C33.7985 22.3774 33.7722 22.4266 33.7697 22.4315L35.5517 23.3395C35.7218 23.0057 35.7818 22.6639 35.8084 22.338C35.8341 22.0238 35.8333 21.6435 35.8333 21.2167H33.8333ZM32.3 24.75C32.7269 24.75 33.1071 24.7508 33.4213 24.7251C33.7472 24.6985 34.089 24.6385 34.4228 24.4684L33.5148 22.6864C33.5099 22.6889 33.4607 22.7152 33.2585 22.7318C33.0446 22.7492 32.7599 22.75 32.3 22.75V24.75ZM33.7697 22.4315C33.7138 22.5413 33.6246 22.6305 33.5148 22.6864L34.4228 24.4684C34.9089 24.2208 35.3041 23.8256 35.5517 23.3395L33.7697 22.4315ZM32.3 18.4167C32.7599 18.4167 33.0446 18.4174 33.2585 18.4349C33.4607 18.4514 33.5099 18.4778 33.5148 18.4802L34.4228 16.6982C34.089 16.5281 33.7472 16.4682 33.4213 16.4416C33.1071 16.4159 32.7269 16.4167 32.3 16.4167V18.4167ZM35.8333 19.95C35.8333 19.5231 35.8341 19.1428 35.8084 18.8286C35.7818 18.5028 35.7218 18.161 35.5517 17.8272L33.7697 18.7352C33.7722 18.7401 33.7985 18.7893 33.8151 18.9915C33.8325 19.2054 33.8333 19.4901 33.8333 19.95H35.8333ZM33.5148 18.4802C33.6246 18.5362 33.7138 18.6254 33.7697 18.7352L35.5517 17.8272C35.3041 17.3411 34.9089 16.9459 34.4228 16.6982L33.5148 18.4802ZM26.2833 16.4167C25.8564 16.4167 25.4761 16.4159 25.1619 16.4416C24.8361 16.4682 24.4943 16.5281 24.1605 16.6982L25.0685 18.4802C25.0734 18.4778 25.1226 18.4514 25.3248 18.4349C25.5387 18.4174 25.8234 18.4167 26.2833 18.4167V16.4167ZM24.75 19.95C24.75 19.4901 24.7508 19.2054 24.7682 18.9915C24.7847 18.7893 24.8111 18.7401 24.8136 18.7352L23.0315 17.8272C22.8615 18.161 22.8015 18.5028 22.7749 18.8286C22.7492 19.1428 22.75 19.5231 22.75 19.95H24.75ZM24.1605 16.6982C23.6744 16.9459 23.2792 17.3411 23.0315 17.8272L24.8136 18.7352C24.8695 18.6254 24.9587 18.5362 25.0685 18.4802L24.1605 16.6982ZM9.87137 10.4285L23.5333 4.96369L22.7906 3.10674L9.12859 8.57153L9.87137 10.4285ZM24.3333 5.50531V9.5H26.3333V5.50531H24.3333ZM23.5333 4.96369C23.9165 4.81042 24.3333 5.09262 24.3333 5.50531H26.3333C26.3333 3.67769 24.4875 2.42798 22.7906 3.10674L23.5333 4.96369Z" fill="#B99BF4" />
              </svg>
            </Flex>
            <AddressText alignItems="center" gap="8px">
              <Text color="#F9F8FA" fontSize={[12, 14, 18]}>{account ? `${account.slice(0, 6)}...${account.slice(-4)}` : ''}</Text>
              <StyledIconButton variant="text" scale="sm" onClick={() => handleCopy(account)}>
                {
                  copied === account
                    ? <CheckmarkCircleFillIcon color="success" width={16} />
                    : <CopyIcon color="textSubtle" width={16} />
                }
              </StyledIconButton>
            </AddressText>
          </Flex>

          <DisconnectWrapper>
            <StyledDisconnectButton
              variant="text"
              onClick={() => {
                logout()
                onDismiss()
              }}
            >
              <Text color="#F9F8FA" fontFamily="IBM Plex Sans" fontSize="15px">Disconnect</Text>
            </StyledDisconnectButton>
          </DisconnectWrapper>

          <QuickBuySection>
            <FlexGap alignItems="center" gap="8px">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect width="48" height="48" rx="24" fill="white" fillOpacity="0.07" />
                <path d="M16.5749 27.7936L25.1121 11.0745C25.1768 10.9477 25.372 10.9928 25.372 11.1345V20.3209C25.372 20.7961 25.8622 21.1211 26.3128 20.9446L30.5568 19.2823C31.1425 19.0529 31.7072 19.654 31.4251 20.2064L22.8879 36.9255C22.8232 37.0523 22.628 37.0072 22.628 36.8655V27.6791C22.628 27.2038 22.1378 26.8789 21.6872 27.0554L17.4432 28.7177C16.8575 28.9471 16.2928 28.346 16.5749 27.7936Z" fill="#EEA23E" />
              </svg>
              <Text color="#F9F8FA" fontFamily="IBM Plex Sans" fontSize="14px" bold>Set Quick Buy Amount</Text>
            </FlexGap>
            <AmountInputContainer>
              {/* <StyledInput 
                type="text"
                value={quickBuyAmount}
                onChange={handleAmountChange}
              /> */}
              <QuickAmount amount={quickBuyAmount} setAmount={handleAmountChange} />
              <CurrencyLogo currency={native} size="23px" />
            </AmountInputContainer>
          </QuickBuySection>

          <TransactionSection>
            <TransactionHeader>
              <Text>Recent transactions</Text>
            </TransactionHeader>
            <WalletTransactions onDismiss={onDismiss} />
          </TransactionSection>

          <DesktopDisconnectWrapper>
            <StyledDisconnectButton
              variant="text"
              onClick={() => {
                logout()
                onDismiss()
              }}
            >
              <Text color="#F9F8FA" fontFamily="IBM Plex Sans" fontSize="15px">Disconnect</Text>
            </StyledDisconnectButton>
          </DesktopDisconnectWrapper>
        </ContentWrapper>
      </StyledModalBody>
    </ModalWrapper>
  )
}

export default WalletModal